import { z } from 'zod';

import { adminListQuerySchema, type AdminAuditAction, type AdminTargetType } from './admin.js';
import { listQuerySchema, type Page } from './common.js';
import { localeSchema } from './enums.js';

/**
 * DESTEK BİLETİ — sözleşme (Faz 8).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden uygulama içi bilet, neden e-posta değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Gerekçenin uzun hâli `schema.prisma`daki `SupportTicket` modelinin başında.
 * Kısası: SMTP bugün kapalı (Yandex 525) ve açıldığında da e-posta,
 * kullanıcının HANGİ çalışma alanından yazdığını, planını ve bağlantılarının
 * durumunu taşımıyor. Bilet kiracıya bağlı olduğu için personel ekranı bu
 * bağlamı sorgulayabiliyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İKİ AYRI OKUYUCU KİTLESİ — tipler de ayrı
 * ═══════════════════════════════════════════════════════════════════════════
 * `SupportTicketDetail` (kullanıcı) ile `AdminSupportTicketDetail` (personel)
 * bilerek iki ayrı tip. Ortak bir tipe `internal?: boolean` eklemek daha kısa
 * olurdu ve tam olarak o kısalık tehlikeli: iç not sızıntısı bir alan
 * unutulunca değil, bir alanın VARLIĞI mümkün olduğu için doğar. Kullanıcı
 * tarafındaki tipte `internal` diye bir alan YOK — sızdırmak için önce tipi
 * değiştirmek gerekir ve o bir gözden geçirmede görülür.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Prisma enum aynaları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prisma enum'larının ayna hâli — `enums.ts`teki desenin aynısı (frontend
 * Prisma client'ı import etmemeli).
 *
 * ⚠️ Aynalar normalde `enums.ts`te yaşıyor; bu üçü burada çünkü o dosya bu
 * turda başka bir yazarın alanında. Destek alanı barrel'a girdiğinde adlar
 * `enums.ts`e taşınabilir — davranış değişmez, yalnızca import yolu.
 */
export const SupportTicketStatus = {
  /// Yeni açıldı, personel henüz bakmadı.
  OPEN: 'OPEN',
  /// Personel yanıtladı, top kullanıcıda.
  WAITING_USER: 'WAITING_USER',
  /// Kullanıcı yanıtladı, top personelde.
  WAITING_STAFF: 'WAITING_STAFF',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
} as const;
export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];

export const SupportTicketPriority = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  /// Para ya da yayın duruyor.
  URGENT: 'URGENT',
} as const;
export type SupportTicketPriority =
  (typeof SupportTicketPriority)[keyof typeof SupportTicketPriority];

export const SupportAuthorRole = {
  USER: 'USER',
  STAFF: 'STAFF',
  /// Otomatik not: durum değişikliği, otomatik kapatma.
  SYSTEM: 'SYSTEM',
} as const;
export type SupportAuthorRole = (typeof SupportAuthorRole)[keyof typeof SupportAuthorRole];

export const SUPPORT_TICKET_STATUSES = [
  'OPEN',
  'WAITING_USER',
  'WAITING_STAFF',
  'RESOLVED',
  'CLOSED',
] as const;

export const SUPPORT_TICKET_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;

/**
 * Bilet kategorisi.
 *
 * Şemada `String`, enum DEĞİL — gerekçe modelin başında: kategori listesi ürün
 * büyüdükçe değişiyor ve her değişiklik migration istemesin. Doğrulama burada,
 * Zod tarafında; yani liste bir kod dağıtımıyla büyüyor.
 */
export const SUPPORT_CATEGORIES = [
  /// Fatura, plan, kredi, ödeme.
  'billing',
  /// Ürün hatası, ekran açılmıyor, sayı yanlış.
  'technical',
  /// Meta/Google bağlantısı, senkron, hesap eşleme.
  'integration',
  /// Özellik isteği.
  'feature',
  'other',
] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Sınırlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 AÇIK BİLET TAVANI — kuyruğu KİRACILAR ARASI paylaşıyoruz.
 *
 * Personel kuyruğu tek ve tüm kiracılardan besleniyor. Döngüye giren bir
 * istemci (ya da sinirli bir kullanıcı) yüz bilet açtığında zarar kendi
 * çalışma alanıyla sınırlı kalmıyor — kuyruğun başındaki BAŞKA bir kiracının
 * gerçek sorunu o yüz satırın altında kayboluyor. Hız sınırı bunu tek başına
 * çözmüyor: dakikada üç bilet de bir günde 4.000 bilet eder.
 *
 * Sayı 10: bir çalışma alanının aynı anda gerçekten bekleyen on ayrı sorunu
 * varsa cevap yeni bir bilet değil, var olanlardan birine yazmak.
 */
export const MAX_OPEN_TICKETS_PER_WORKSPACE = 10;

/// Mesaj gövdesi üst sınırı. Ek dosya akışı YOK (şemadaki `attachments` bugün
/// boş); log yapıştıran kullanıcı için 8.000 karakter yeterli, 100 KB'lık bir
/// yığın ise okunmaz ve tabloyu şişirir.
export const SUPPORT_MESSAGE_MAX_LENGTH = 8_000;

// ═══════════════════════════════════════════════════════════════════════════
// Kullanıcı tarafı — girdi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 KULLANICI `URGENT` SEÇEMEZ.
 *
 * Personel kuyruğu `priority` ile sıralanıyor. Kullanıcıya "acil" düğmesi
 * verildiğinde herkes onu seçer — bu bir tahmin değil, her destek ürününde
 * yaşanan şey — ve sıralama bir şey ifade etmeyi bırakır. O noktada kuyruk
 * yalnızca tarihe göre sıralı bir listeye döner ve gerçekten para duran bilet
 * sıradan bir soru ile aynı yerde bekler.
 *
 * `URGENT` yalnız personelin `PATCH /admin/support/tickets/:id` ile
 * verebildiği bir işaret. Kullanıcının aciliyeti `HIGH` ile ifade ediliyor ve
 * kategorisi (`billing`) zaten ikinci bir sinyal.
 */
export const supportUserPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH']).default('NORMAL');

export const supportTicketCreateSchema = z.object({
  subject: z.string().trim().min(4).max(160),
  category: z.enum(SUPPORT_CATEGORIES),
  /**
   * İlk mesaj. Bilet ile mesajı AYRI iki istekte almıyoruz: gövdesiz bir
   * bilet personel kuyruğunda "konu var, sorun yok" satırı olarak görünürdü
   * ve ilk mesaj hiç yazılmadan sekme kapanırsa kalıcı olarak öyle kalırdı.
   *
   * 🔴 Alan adı `body` — bir dönem `message` idi. Yanıt yazma uçları
   * (`supportMessageCreateSchema`, `adminSupportMessageCreateSchema`) ve
   * dönen mesaj nesnesi hep `body` diyordu; tek kavramın iki adı olması,
   * istemcinin hangi uçta hangi adı kullanacağını EZBERLEMESİ demekti.
   * Alt sınır burada 10 karakter (yanıtlarda 1): ilk mesaj sorunu anlatmak
   * zorunda, "merhaba" diyen bir bilet personel için kayıp bir tur.
   */
  body: z.string().trim().min(10).max(SUPPORT_MESSAGE_MAX_LENGTH),
  priority: supportUserPrioritySchema,
});
export type SupportTicketCreateInput = z.infer<typeof supportTicketCreateSchema>;

export const supportMessageCreateSchema = z.object({
  body: z.string().trim().min(1).max(SUPPORT_MESSAGE_MAX_LENGTH),
});
export type SupportMessageCreateInput = z.infer<typeof supportMessageCreateSchema>;

/**
 * Liste filtresi.
 *
 * `active` gerçek bir durum DEĞİL, üç durumun kısayolu
 * (`OPEN` + `WAITING_USER` + `WAITING_STAFF`). Listenin varsayılan sekmesi
 * "açık olanlar" ve o sekme üç durumu birden kapsıyor; kısayol olmasaydı
 * arayüz ya üç ayrı istek atardı ya da sunucu varsayılanda kapanmış biletleri
 * de döndürürdü.
 */
export const supportTicketStatusFilterSchema = z.enum([...SUPPORT_TICKET_STATUSES, 'active']);
export type SupportTicketStatusFilter = z.infer<typeof supportTicketStatusFilterSchema>;

export const supportTicketListQuerySchema = listQuerySchema.extend({
  status: supportTicketStatusFilterSchema.optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
});
export type SupportTicketListQuery = z.infer<typeof supportTicketListQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kullanıcı tarafı — çıktı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mesaj — KULLANICI görünümü.
 *
 * `internal` alanı YOK ve bu tipin en önemli özelliği bu (dosya başındaki
 * nota bak). İç notlar sunucuda filtreleniyor; tip de onları taşıyamıyor.
 */
export interface SupportMessageRow {
  id: string;
  authorRole: SupportAuthorRole;
  /// Yazarın görünen adı. Personel mesajlarında gerçek ad DEĞİL, sabit bir
  /// takım adı dönüyor — gerekçe `SUPPORT_STAFF_DISPLAY_NAME` üzerinde.
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface SupportTicketRow {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  /// Liste sıralamasının kaynağı. `updatedAt` DEĞİL — durum değişikliği de
  /// onu tazeliyor ve "en son yazılan bilet" sıralaması bozuluyordu.
  lastMessageAt: string;
  /// Kullanıcı için okunmamış yanıt var mı (`userUnread`). Rozetin kaynağı.
  unread: boolean;
  messageCount: number;
  createdAt: string;
}

export interface SupportTicketDetail extends SupportTicketRow {
  messages: SupportMessageRow[];
  /// Kapanmış bilete yazılamaz; arayüz yazma kutusunu buna bakarak kapatıyor
  /// (durum listesini tekrar yorumlamak zorunda kalmasın).
  canReply: boolean;
}

/**
 * Liste yanıtı.
 *
 * `unreadCount` sayfalamadan BAĞIMSIZ: sol menüdeki rozet "bu sayfada kaç
 * okunmamış var" değil, "toplam kaç" demek. Sayfa içinden hesaplanamaz.
 */
export interface SupportTicketListResponse extends Page<SupportTicketRow> {
  unreadCount: number;
  /// Bu çalışma alanında yeni bilet açılabilir mi
  /// (`MAX_OPEN_TICKETS_PER_WORKSPACE`). Arayüz düğmeyi önceden kapatabilsin
  /// diye — 409'u kullanıcıya göstermek yerine.
  canOpenNew: boolean;
}

/**
 * Personel mesajlarında görünen ad.
 *
 * Gerçek ad DÖNMÜYOR ve bu bir gizlilik kararı: destek yanıtı veren kişi
 * platform çalışanı; adı ve e-postası kiracıya açılırsa geri alınamaz.
 * Kullanıcının ihtiyacı "bu satırı bir insan mı yazdı" bilgisi, kim olduğu
 * değil. Arayüz bu dizeyi çeviri anahtarı olarak da kullanabilir.
 */
export const SUPPORT_STAFF_DISPLAY_NAME = 'Mentorivo Support';

// ═══════════════════════════════════════════════════════════════════════════
// Personel tarafı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Personel kuyruğu filtresi.
 *
 * ⚠️ `q` çalışma alanı adında DEĞİL, bilet KONUSUNDA ve mesaj gövdesinde
 * arıyor. Çalışma alanı için ayrı bir `workspaceId` filtresi var: "şu kiracının
 * tüm biletleri" ile "şu kelimeyi içeren biletler" iki farklı soru ve tek
 * kutuya sıkıştırıldığında ikisi de yanlış cevaplanıyor.
 */
export const adminSupportTicketListQuerySchema = adminListQuerySchema.extend({
  status: supportTicketStatusFilterSchema.optional(),
  priority: z.enum(SUPPORT_TICKET_PRIORITIES).optional(),
  category: z.enum(SUPPORT_CATEGORIES).optional(),
  workspaceId: z.uuid().optional(),
});
export type AdminSupportTicketListQuery = z.infer<typeof adminSupportTicketListQuerySchema>;

export const adminSupportMessageCreateSchema = z.object({
  body: z.string().trim().min(1).max(SUPPORT_MESSAGE_MAX_LENGTH),
  /**
   * İç not — kullanıcıya GÖRÜNMEZ.
   *
   * Varsayılan `false` bilinçli ve tersi tehlikeli olurdu: varsayılan `true`
   * olsaydı, bayrağı göndermeyi unutan bir istemci kullanıcının beklediği
   * yanıtı sessizce iç nota çevirirdi — kullanıcı cevapsız kalır, personel
   * "yazdım" sanırdı. Yanlış yöne düşen hata, notun görünmesi değil,
   * yanıtın görünmemesi.
   */
  internal: z.boolean().default(false),
  /**
   * Yanıtla birlikte durumu değiştir. Ayrı bir `PATCH` atmaya gerek kalmasın
   * diye: "cevapladım ve çözüldü" tek bir eylem ve iki istek arasında pencere
   * bırakmak, ikincisi düşerse biletin yanlış durumda kalması demek.
   */
  status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
});
export type AdminSupportMessageCreateInput = z.infer<typeof adminSupportMessageCreateSchema>;

/// Durum/öncelik değişikliği. İkisinden en az biri zorunlu — boş bir PATCH
/// denetim kaydına "hiçbir şey değişmedi" satırı yazardı.
export const adminSupportTicketPatchSchema = z
  .object({
    status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
    priority: z.enum(SUPPORT_TICKET_PRIORITIES).optional(),
  })
  .refine((v) => v.status !== undefined || v.priority !== undefined, {
    message: 'En az bir alan gerekli: status ya da priority',
    path: ['status'],
  });
export type AdminSupportTicketPatchInput = z.infer<typeof adminSupportTicketPatchSchema>;

/// Mesaj — PERSONEL görünümü. `internal` yalnız burada var.
export interface AdminSupportMessageRow extends SupportMessageRow {
  internal: boolean;
  authorUserId: string | null;
  /// Kullanıcı mesajlarında yazarın e-postası; personel/sistem satırlarında
  /// `null`. Personel ekranı "kim yazdı"yı bilmek zorunda.
  authorEmail: string | null;
}

export interface AdminSupportTicketRow {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  lastMessageAt: string;
  createdAt: string;
  /// Personel için okunmamış (`staffUnread`).
  unread: boolean;
  messageCount: number;
  workspaceId: string;
  workspaceName: string | null;
  createdByEmail: string | null;
  /// Biletin açılışından bu yana geçen saat. Sunucuda hesaplanıyor ki liste
  /// SSR ile CSR arasında farklı bir sayı göstermesin (`ResponseMeta`
  /// başlığındaki `generatedAt` gerekçesinin aynısı).
  ageHours: number;
}

export interface AdminSupportTicketDetail extends AdminSupportTicketRow {
  messages: AdminSupportMessageRow[];
  /// Kiracının bağlamı — personelin ilk bakışta görmesi gereken şey. Biletin
  /// e-postaya karşı asıl üstünlüğü bu (dosya başındaki nota bak).
  context: {
    plan: string | null;
    subscriptionStatus: string | null;
    memberCount: number;
    connectionCount: number;
    /// Kopuk ya da bozuk bağlantı sayısı — `integration` kategorili biletlerin
    /// yarısının cevabı burada.
    brokenConnectionCount: number;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 Denetim sözlüğü köprüsü — GEÇİCİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Personel işlemlerinin denetim eylemleri.
 *
 * Bunların DOĞRU yeri `admin.ts`teki `ADMIN_AUDIT_ACTION` / `ADMIN_TARGET_TYPE`
 * sözlükleri; o dosya bu turda başka bir yazarın alanında ve tek yazarlı.
 * Sözlükler kapalı birleşim (union) tipi olduğu için yeni bir dize doğrudan
 * `AdminAuditService.record()` imzasına geçmiyor.
 *
 * Bu yüzden dönüşüm TEK YERDE, aşağıdaki iki sabitte yapılıyor — çağrı
 * yerlerine dağılmış `as` ifadeleri yerine. Değerlerin biçimi sözlüğün
 * kuralına uyuyor (`admin.` öneki): `/admin/audit` ekranı satırları
 * `startsWith('admin.')` ile ayırıyor, yani köprü olmadan da doğru tarafta
 * görünüyorlar.
 *
 * 🔧 `admin.ts` açıldığında yapılacak: üç eylem ve `support_ticket` hedef tipi
 * oradaki sözlüklere taşınır, buradaki iki sabit silinir, çağrı yerleri
 * `ADMIN_AUDIT_ACTION.SUPPORT_*` yazar. Davranış değişmez.
 */
export const SUPPORT_ADMIN_AUDIT_ACTION = {
  TICKET_REPLIED: 'admin.support.ticket_replied',
  TICKET_STATUS_CHANGED: 'admin.support.ticket_status_changed',
  TICKET_PRIORITY_CHANGED: 'admin.support.ticket_priority_changed',
} as const;

/// Köprünün kendisi — `AdminAuditAction` birleşimine tek dönüşüm noktası.
export const supportAdminAuditAction = SUPPORT_ADMIN_AUDIT_ACTION as unknown as Record<
  keyof typeof SUPPORT_ADMIN_AUDIT_ACTION,
  AdminAuditAction
>;

export const SUPPORT_ADMIN_TARGET_TYPE = 'support_ticket' as unknown as AdminTargetType;

// ═══════════════════════════════════════════════════════════════════════════
// Yardım merkezi — uç sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Makale METİNLERİ bu dosyada DEĞİL.
 *
 * `help-center.ts` on makalenin TR + EN tam gövdesini taşıyor (~600 satır) ve
 * kök barrel'dan export EDİLMİYOR — `mail.ts`, `template-seed.ts` ve
 * `demo-data.ts` ile aynı gerekçe. Burada yalnız uç sözleşmesi var; arayüz
 * makaleleri sunucudan çekiyor, bundle'ına gömmüyor.
 */
export const HELP_CATEGORIES = [
  'getting-started',
  'connections',
  'automation',
  'billing',
  'notifications',
  'team',
  'data',
  'support',
] as const;
export type HelpCategory = (typeof HELP_CATEGORIES)[number];

export const helpArticleListQuerySchema = z.object({
  category: z.enum(HELP_CATEGORIES).optional(),
  /**
   * Serbest arama — başlık, özet ve gövdede geçiyor mu.
   *
   * `common.ts`teki `searchParam` KULLANILMADI: o `.trim().min(1)` ile boş
   * dizeyi reddediyor ve arama kutusu boşaltıldığında istemci `?q=` gönderiyor.
   * Burada boş `q` "arama yok" demek, 400 değil.
   */
  q: z
    .string()
    .max(120)
    .optional()
    .transform((v) => {
      const trimmed = v?.trim() ?? '';
      return trimmed.length > 0 ? trimmed : undefined;
    }),
  /// Verilmezse kullanıcının `User.locale` değeri kullanılır.
  locale: localeSchema.optional(),
});
export type HelpArticleListQuery = z.infer<typeof helpArticleListQuerySchema>;

export const helpArticleDetailQuerySchema = z.object({
  locale: localeSchema.optional(),
});
export type HelpArticleDetailQuery = z.infer<typeof helpArticleDetailQuerySchema>;

export interface HelpArticleSummary {
  slug: string;
  category: HelpCategory;
  title: string;
  summary: string;
  /// Makalenin anlattığı panel ekranı (`/dashboard/...`). `null` = ekrana
  /// bağlı olmayan kavramsal makale. Ham URL değil ROTA saklanıyor; arayüz
  /// dili ve ön eki kendisi ekliyor.
  route: string | null;
}

export interface HelpArticleDetail extends HelpArticleSummary {
  paragraphs: string[];
  /**
   * Aynı kategorideki diğer makaleler (en fazla 3).
   *
   * Sunucuda hesaplanıyor çünkü liste ucu ile detay ucu ayrı: arayüz "ilgili
   * makaleler" için tüm kataloğu ikinci kez çekmek zorunda kalmasın.
   */
  related: HelpArticleSummary[];
}

export interface HelpCategoryCount {
  key: HelpCategory;
  count: number;
}

export interface HelpArticleListResponse {
  articles: HelpArticleSummary[];
  /// Kategori sekmelerinin kaynağı. Sayılar FİLTRESİZ katalogdan geliyor —
  /// filtre uygulandığında sekmelerin hepsinin sıfıra düşmesi, kullanıcının
  /// filtreyi kaldıracak yeri bulamaması demekti.
  categories: HelpCategoryCount[];
  /// Yanıtın hangi dilde döndüğü. Arayüz istediği dili alamadıysa (katalogda
  /// o dil yoksa) bunu görebilsin.
  locale: 'en' | 'tr';
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hatalar
// ═══════════════════════════════════════════════════════════════════════════

export const SupportErrorCode = {
  /// Bilet yok ya da başka bir kiracıya ait — ikisi de aynı yanıt (404).
  TICKET_NOT_FOUND: 'TICKET_NOT_FOUND',
  /**
   * Bilet `CLOSED` — yazılamaz. 409, 400 değil: istek geçerli, durum çakışıyor.
   *
   * `RESOLVED` bu hatayı VERMEZ; kullanıcının "aslında çözülmemiş" demesi
   * meşru bir hareket ve bilet `WAITING_STAFF`a geri döner. Kapalı bilet ise
   * yeniden açılmıyor (şema kararı) — arayüz yeni bilet açtırmalı.
   */
  TICKET_CLOSED: 'TICKET_CLOSED',
  /// Açık bilet tavanı doldu. `details.limit` ve `details.open` dolu. HTTP 409.
  TOO_MANY_OPEN_TICKETS: 'TOO_MANY_OPEN_TICKETS',
  ARTICLE_NOT_FOUND: 'ARTICLE_NOT_FOUND',
} as const;
export type SupportErrorCode = (typeof SupportErrorCode)[keyof typeof SupportErrorCode];
